import React, { useState, useEffect, useCallback } from 'react';
import FormContents from 'components/atoms/Form/FormContents';
import { Link } from 'react-router-dom';
import PrimaryButton from 'components/atoms/Button';
import YearPicker from 'components/molecules/YearPicker/YearPicker';
import moment from 'moment';
import {
  OutputType,
  exportCSVDailyInputFile,
  exportCSVMonthlyFileToBudgetYearInput,
  exportFileExcelToBudgetYear,
} from 'api/salesBudgetYear';
import useToastNotification from 'hooks/useToastNotification';
import BlockUI from 'components/molecules/BlockUi';
import OrganizationFilteredTree from 'components/organismos/organization/OrganizationFilteredTree/OrganizationFilteredTree';
import useCurrentRoleScreen from 'hooks/useCurrentRoleScreen';
import Dropdown from 'components/molecules/Dropdown';
import productColor, { grayScale } from 'components/styles';
import Icon from 'components/atoms/Icon';
import BodyText from 'components/atoms/BodyText';
import { css } from '@emotion/core';
import ConfirmModal from 'components/organismos/ConfirmModal';
import BudgetYearTable from './BudgetYearTable';
import useSalesBudgetYear, { createFileName, downloadCSVFile, downloadOriginalFile, useGetFiscalYear2 } from './hooks';
import { getOrgTargetDates } from 'utility/orgTargetDateUtil';

const SalesBudgetYearInputLayout: React.FC<{
  getFiscalYear: number;
}> = ({ getFiscalYear }) => {
  const [getDisableValue, setDisableValue] = useState(false);
  const dateSelect = sessionStorage.getItem('dateSelect') || getFiscalYear;
  const [selectedYear, setSelectedYear] = useState(Number(dateSelect));
  const [orgName, setOrgName] = useState(
    sessionStorage.getItem('destination_org') || sessionStorage.getItem('loginUser.orgName') || '',
  );
  const [getSupportDestinationOrg, setSupportDestinationOrg] = useState(
    sessionStorage.getItem('orgCode_DestinationOrg') || 'all',
  );

  const staffCode = sessionStorage.getItem('loginUser.staffCode') || '';

  // notification
  const { errorNotification } = useToastNotification();
  const {
    formik,
    getRows,
    getIsLoading,
    setIsLoading,
    categoryAry,
    setCategoryAry,
    canRegist,
    confirmModalOpen,
    closeConfirmModal,
    onSubmit,
  } = useSalesBudgetYear(selectedYear, getSupportDestinationOrg);

  const { fiscalStartEnd } = useGetFiscalYear2(Number(selectedYear));

  // export
  const handleExcelExport = () => {
    const targetYear = `${selectedYear}/01/01`;
    const procDateStr = moment(new Date()).format('YYYYMMDDHHmmss');
    setIsLoading(true);
    setDisableValue(true);
    exportFileExcelToBudgetYear(getSupportDestinationOrg, orgName, targetYear, procDateStr, categoryAry)
      .then((response) => {
        const fileName = createFileName(getSupportDestinationOrg, '年間月次予算', selectedYear, '.xlsx');
        downloadOriginalFile(response, fileName);
        setIsLoading(false);
        setDisableValue(false);
      })
      .catch(() => {
        setIsLoading(false);
        setDisableValue(false);
        errorNotification('サーバー側でエラーが発生しました。');
      });
  };

  const handleMonthlyCSVExport = () => {
    const targetYear = `${selectedYear}/01/01`;
    setIsLoading(true);
    setDisableValue(true);
    exportCSVMonthlyFileToBudgetYearInput({ orgCode: getSupportDestinationOrg, targetYear, categoryAry })
      .then((response) => {
        const fileName = createFileName(getSupportDestinationOrg, '年間月次予算', selectedYear, '.csv');
        downloadCSVFile(response, fileName);
        setIsLoading(false);
        setDisableValue(false);
      })
      .catch(() => {
        setIsLoading(false);
        setDisableValue(false);
        errorNotification('サーバー側でエラーが発生しました。');
      });
  };

  const handleDailyCSVExport = () => {
    const targetYear = `${selectedYear}/01/01`;
    setIsLoading(true);
    setDisableValue(true);
    exportCSVDailyInputFile({
      outputType: OutputType.YEARLY,
      orgCode: getSupportDestinationOrg,
      targetMonth: targetYear,
      categoryAry,
    })
      .then((response) => {
        const fileName = createFileName(getSupportDestinationOrg, '年間日次予算', selectedYear, '.csv');
        downloadCSVFile(response, fileName);
        setIsLoading(false);
        setDisableValue(false);
      })
      .catch(() => {
        setIsLoading(false);
        setDisableValue(false);
        errorNotification('サーバー側でエラーが発生しました。');
      });
  };

  // 初回表示時にsessionStorageにパラメータの会計年度を格納
  if (getFiscalYear && !sessionStorage.getItem('dateSelect')) {
    sessionStorage.setItem('dateSelect', `${getFiscalYear}`);
  }

  sessionStorage.setItem('orgCode_DestinationOrg', String(getSupportDestinationOrg));
  sessionStorage.setItem('destination_org', orgName);
  sessionStorage.setItem('fiscalStartEnd.Start', fiscalStartEnd.start.toString());
  sessionStorage.setItem('fiscalStartEnd.End', fiscalStartEnd.end.toString());

  const roleScreen = useCurrentRoleScreen('/salesBudgetYearInput');

  const staffAccess =
    roleScreen &&
    roleScreen.useStaffAccess === true &&
    (roleScreen.staffAccessFunctionRoles || []).filter((role: any) => role.orgCode === getSupportDestinationOrg);

  const checkUserRole =
    getSupportDestinationOrg !== 'all' && staffAccess && staffAccess.length > 0 ? staffAccess[0] : roleScreen;

  const orgCode = sessionStorage.getItem('orgCode_DestinationOrg');

  const enableDownloadDaily = orgCode;

  // 集計用の日付を計算
  const getOrgTargetDays = useCallback(() => {
    if (!fiscalStartEnd.start || !fiscalStartEnd.end) {
      return { from: new Date(), to: new Date() };
    }
    return getOrgTargetDates(fiscalStartEnd.start, fiscalStartEnd.end);
  }, [fiscalStartEnd.start, fiscalStartEnd.end]);

  const orgTargetDates = getOrgTargetDays();
  const orgTargetDayFrom = orgTargetDates.from;
  const orgTargetDayTo = orgTargetDates.to;

  return (
    <div>
      <BlockUI blocking={getIsLoading}>
        <FormContents>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <YearPicker
              selectedYear={selectedYear}
              onChange={(year: number) => {
                setSelectedYear(year);
                sessionStorage.setItem('dateSelect', `${year}`);
              }}
              setAutoLeft={false}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '20px',
              }}
            >
              {checkUserRole && checkUserRole.importFlag === 1 && (
                <Link to="/salesBudgetYearInputImport">
                  <PrimaryButton text="インポート " />
                </Link>
              )}

              {checkUserRole && checkUserRole.downloadFlag === 1 && (
                <Dropdown
                  disabled={getDisableValue}
                  items={[
                    {
                      value: '0',
                      label: '月次情報ダウンロード（年間）',
                      onClick: handleMonthlyCSVExport,
                    },
                    ...(enableDownloadDaily
                      ? [
                          {
                            value: '1',
                            label: '日次情報ダウンロード（年間）',
                            onClick: handleDailyCSVExport,
                          },
                        ]
                      : []),
                  ]}
                >
                  <Icon type="download" color={grayScale.gray100} />
                  <BodyText
                    color={productColor.primary}
                    customStyle={css({
                      marginLeft: '4px',
                    })}
                  >
                    CSVダウンロード
                  </BodyText>
                </Dropdown>
              )}
            </div>
          </div>

          <OrganizationFilteredTree
            functionType={3}
            staffCode={staffCode}
            orgLabel="店舗名"
            initOrgValue={String(getSupportDestinationOrg)}
            orgCallback={(args: string | Array<string>) => {
              setSupportDestinationOrg(String(args));
            }}
            orgCategoryCallback={(args: Array<string>) => {
              setCategoryAry(args);
            }}
            orgNameCallback={(args: string | Array<string>) => {
              setOrgName(String(args));
            }}
            targetdayForm={orgTargetDayFrom}
            targetdayTo={orgTargetDayTo}
            addAllItem={true}
            isAlignItemsCenter={true}
            apiVersion="v6"
            menuId={roleScreen?.menuId}
          />
        </FormContents>
        <FormContents>
          <BudgetYearTable
            getSupportDestinationOrg={getSupportDestinationOrg}
            selectedYear={selectedYear}
            categoryAry={categoryAry}
            values={formik.values}
            rows={getRows}
            formik={formik}
            isReadOnly={getSupportDestinationOrg === 'all'}
            onInputChange={(field: string, value: any) => {
              formik.setFieldValue(field, value);
            }}
          />
        </FormContents>

        {/* 登録ボタン */}
        {canRegist && checkUserRole && checkUserRole.usabilityGeneralItem1 === 1 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '20px',
              marginBottom: '20px',
            }}
          >
            <PrimaryButton text="更新" onClick={formik.handleSubmit} />
          </div>
        )}
      </BlockUI>

      {/* 確認モーダル */}
      <ConfirmModal
        title="確認メッセージ"
        content="更新します。よろしいですか？"
        open={confirmModalOpen}
        closeHandler={closeConfirmModal}
        onSubmit={onSubmit}
        submitText="更新"
        items={[]}
      />
    </div>
  );
};
export default SalesBudgetYearInputLayout;
